import type { Tag, Todo } from '@/api'
import type { RouteLocationRaw } from 'vue-router'
import { matchText } from './fuzzy'
import { QUOTE_CHARS, formatHashtag, hashtagTokens, isQuote, isValidTagName } from './hashtags'

export const MAX_SUGGESTIONS = 6

// Every category/tag used by any loaded todo, most used first (ties alphabetically). Case variants
// count as one entry, shown the way they were most often written.
export function collectTags(todosByCalendar: Record<number, Todo[]>): string[] {
  const counts = new Map<string, Map<string, number>>()
  for (const todos of Object.values(todosByCalendar)) {
    for (const todo of todos) {
      for (const tag of todo.categories) {
        const variants = counts.get(tag.toLowerCase()) ?? new Map<string, number>()
        variants.set(tag, (variants.get(tag) ?? 0) + 1)
        counts.set(tag.toLowerCase(), variants)
      }
    }
  }
  const ranked = [...counts.values()].map((variants) => {
    const spellings = [...variants.entries()].sort(
      (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
    )
    const total = spellings.reduce((sum, [, n]) => sum + n, 0)
    return { tag: spellings[0][0], total }
  })
  ranked.sort((a, b) => b.total - a.total || a.tag.localeCompare(b.tag))
  return ranked.map((r) => r.tag)
}

// Suggestions for what is being typed: not yet selected, prefix matches before other substring
// matches, order otherwise kept from `known` (i.e. by usage). An empty query suggests the top ones.
export function suggestTags(
  known: string[],
  selected: string[],
  query: string,
  limit: number = MAX_SUGGESTIONS,
): string[] {
  const taken = new Set(selected.map((s) => s.toLowerCase()))
  const needle = query.trim().toLowerCase()
  const free = known.filter((tag) => !taken.has(tag.toLowerCase()))
  if (!needle) return free.slice(0, limit)
  const prefix = free.filter((tag) => tag.toLowerCase().startsWith(needle))
  const inside = free.filter(
    (tag) => !tag.toLowerCase().startsWith(needle) && tag.toLowerCase().includes(needle),
  )
  return [...prefix, ...inside].filter((tag) => tag.toLowerCase() !== needle).slice(0, limit)
}

// Every name worth offering for a hashtag: the user's own tags first, then their aliases (another writing
// that becomes the tag on the next sync), then the texts used on todos that are neither. Case variants count
// as one, the first spelling wins; the built-in subtask tag is not offered.
export function tagNames(known: string[], tags: Tag[]): string[] {
  const names: string[] = []
  const seen = new Set<string>()
  const add = (name: string): void => {
    if (!seen.has(name.toLowerCase())) {
      seen.add(name.toLowerCase())
      names.push(name)
    }
  }
  const own = tags.filter((tag) => !tag.internal)
  for (const tag of own) add(tag.name)
  for (const tag of own) for (const alias of tag.aliases) add(alias.alias)
  for (const name of known) add(name)
  return names
}

export interface Hashtag {
  start: number // (index of the `#`)
  end: number // (index after its last character, or after the closing quote/bracket)
  query: string // (the name typed so far: the text between the `#` (or the opening quote) and the caret)
  full: string // (the whole name of the tag around the caret, also what is behind it; same as `query` if unfinished)
}

// (`QUOTE_CHARS` has no character that is special in a regex character class)
const UNESCAPE = new RegExp(`\\\\([${QUOTE_CHARS}\\\\])`, 'gu')
const OPEN_QUOTED = new RegExp(
  `(?:^|[\\s,;])#[${QUOTE_CHARS}]((?:[^${QUOTE_CHARS}\\\\\\r\\n]|\\\\[${QUOTE_CHARS}\\\\]|\\\\)*)$`,
  'u',
)

function unescapeQuoted(raw: string): string {
  return raw.replace(UNESCAPE, '$1')
}

// The hashtag that the caret is in or right behind, in any of the forms of `hashtags.ts`: `#word` (letters, digits,
// underscores), `#"Tag with Spaces"` and `#[Tag with Spaces]`, finished or still being typed (no closing quote or bracket
// yet), and an empty `#""`/`#[]` with the caret inside. The range ends behind the closing quote or bracket where there is
// one, so replacing it replaces the whole tag. None if the caret is not in a hashtag.
export function hashtagAt(text: string, caret: number): Hashtag | null {
  // a finished quoted or bracketed tag around the caret
  for (const token of hashtagTokens(text, { lenient: true })) {
    if (
      token.start < caret &&
      caret <= token.end &&
      (isQuote(text[token.start + 1]) || text[token.start + 1] === '[')
    ) {
      const contentStart = token.start + 2
      if (caret < contentStart) break
      const upTo = Math.min(caret, token.end - 1)
      return {
        start: token.start,
        end: token.end,
        query: unescapeQuoted(text.slice(contentStart, upTo)),
        full: token.name,
      }
    }
  }
  const before = text.slice(0, caret)
  // one that is still open: no unescaped closing quote / no closing bracket before the caret
  const quoted = OPEN_QUOTED.exec(before)
  if (quoted) {
    return {
      start: caret - 2 - quoted[1].length,
      end: isQuote(text[caret]) ? caret + 1 : caret,
      query: unescapeQuoted(quoted[1]),
      full: unescapeQuoted(quoted[1]),
    }
  }
  const bracketed = /(?:^|[\s,;])#\[([^\]\r\n]*)$/u.exec(before)
  if (bracketed) {
    return {
      start: caret - 2 - bracketed[1].length,
      end: text[caret] === ']' ? caret + 1 : caret,
      query: bracketed[1],
      full: bracketed[1],
    }
  }
  const word = before.match(/(?:^|\s)#([\p{L}\p{N}_]*)$/u)
  if (!word) return null
  const start = caret - word[1].length - 1
  const after = text.slice(caret).match(/^[\p{L}\p{N}_]*/u)?.[0] ?? ''
  return { start, end: caret + after.length, query: word[1], full: word[1] + after }
}

// The text with the hashtag replaced by the tag in the form `formatHashtag` picks for the name (`#word`,
// `#"Tag with Spaces"`, `#[She said "Hi"]`), a space behind it unless one follows already, and where the caret belongs
// afterwards.
export function applyHashtag(
  text: string,
  hashtag: Hashtag,
  name: string,
): { text: string; caret: number } {
  const rest = text.slice(hashtag.end)
  const tag = formatHashtag(name)
  return {
    text: text.slice(0, hashtag.start) + tag + (rest.startsWith(' ') ? '' : ' ') + rest,
    caret: hashtag.start + tag.length + 1,
  }
}

export const MAX_HASHTAG_SUGGESTIONS = 5

export interface HashtagSuggestion {
  name: string
  ranges: [number, number][] // (matched index ranges in `name`, to highlight)
}

function matchName(
  name: string,
  needle: string,
): { tier: number; ranges: [number, number][] } | null {
  if (name.toLowerCase() === needle) return null
  return matchText(name, needle)
}

// What to offer while a hashtag is typed in the add field. A bare `#` gives the most used tags (`usage`, most used
// first; tags nobody uses follow in the order of `names`). A query matches as a prefix, then anywhere in a row, then
// as its letters in order with gaps (`socg` finds `SomeCoolTag`), each tier by usage; the tag that equals the query is
// not offered. Each suggestion says which letters matched.
export function suggestHashtags(
  names: string[],
  usage: string[],
  query: string,
  limit: number = MAX_HASHTAG_SUGGESTIONS,
): HashtagSuggestion[] {
  const rank = new Map<string, number>()
  usage.forEach((name, index) => {
    if (!rank.has(name.toLowerCase())) rank.set(name.toLowerCase(), index)
  })
  const used = (name: string): number => rank.get(name.toLowerCase()) ?? Number.MAX_SAFE_INTEGER
  const needle = query.trim().toLowerCase()
  const entries = names.map((name, index) => ({ name, index, used: used(name) }))
  const byUsage = (a: { used: number; index: number }, b: { used: number; index: number }) =>
    a.used - b.used || a.index - b.index
  if (!needle) {
    return entries
      .sort(byUsage)
      .slice(0, limit)
      .map(({ name }) => ({ name, ranges: [] }))
  }
  const matched = entries.flatMap((entry) => {
    const match = matchName(entry.name, needle)
    return match ? [{ ...entry, ...match }] : []
  })
  matched.sort((a, b) => a.tier - b.tier || byUsage(a, b))
  return matched.slice(0, limit).map(({ name, ranges }) => ({ name, ranges }))
}

// A name split into the parts that matched (to show in bold) and the ones in between.
export function highlightParts(
  name: string,
  ranges: [number, number][],
): { text: string; hit: boolean }[] {
  const parts: { text: string; hit: boolean }[] = []
  let position = 0
  for (const [start, end] of ranges) {
    if (start > position) parts.push({ text: name.slice(position, start), hit: false })
    parts.push({ text: name.slice(start, end), hit: true })
    position = end
  }
  if (position < name.length) parts.push({ text: name.slice(position), hit: false })
  return parts.length ? parts : [{ text: name, hit: false }]
}

// Tag texts used on todos that could become an alias: not yet a defined tag or alias (ignoring case)
// and a valid name (see `isValidTagName`, a name with spaces is fine). Order of `known` kept.
export function aliasCandidates(known: string[], tags: Tag[]): string[] {
  const taken = new Set<string>()
  for (const tag of tags) {
    taken.add(tag.name.toLowerCase())
    for (const alias of tag.aliases) taken.add(alias.alias.toLowerCase())
  }
  return known.filter((name) => isValidTagName(name) && !taken.has(name.toLowerCase()))
}

// The tags of a todo with `from` taken out and put back at the final index `to` (a copy; out of range = unchanged).
export function moveTag(tags: string[], from: number, to: number): string[] {
  if (from < 0 || from >= tags.length || to < 0 || to >= tags.length || from === to)
    return [...tags]
  const result = [...tags]
  const [moved] = result.splice(from, 1)
  result.splice(to, 0, moved)
  return result
}

// The tags with two places exchanged (a copy; out of range = unchanged).
export function swapTags(tags: string[], a: number, b: number): string[] {
  if (a < 0 || b < 0 || a >= tags.length || b >= tags.length) return [...tags]
  const result = [...tags]
  ;[result[a], result[b]] = [result[b], result[a]]
  return result
}

export type TagZone = 'category' | 'tags'

// The order after dragging a chip in the tag editor, which shows the first tag in a "category" slot and the rest as
// plain tags (indexes are inside their zone). Onto the slot swaps with the current category, within the tags the chip
// moves (never into the first place, that is the slot), out of the slot the next tag becomes the category.
export function dropTag(
  tags: string[],
  fromZone: TagZone,
  fromIndex: number,
  toZone: TagZone,
  toIndex: number,
): string[] {
  const from = fromZone === 'category' ? 0 : fromIndex + 1
  if (fromZone === 'category' && toZone === 'category') return [...tags]
  if (toZone === 'category') return swapTags(tags, from, 0)
  if (fromZone === 'category') return moveTag(tags, 0, Math.min(toIndex, tags.length - 1))
  return moveTag(tags, from, Math.min(Math.max(1, toIndex + 1), tags.length - 1))
}

export interface TagStyle {
  name: string
  color: string | null
  aliases: { alias: string }[]
}

// The color of the user's tag with this name (or alias), ignoring case; none if it has no color.
export function colorForTag(tags: TagStyle[], name: string): string | null {
  const key = name.toLowerCase()
  const tag = tags.find(
    (t) => t.name.toLowerCase() === key || t.aliases.some((a) => a.alias.toLowerCase() === key),
  )
  return tag?.color ?? null
}

// Black or white, whichever reads better on the background `#rrggbb`.
export function readableTextColor(background: string): string {
  const hex = background.replace('#', '')
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
  const linear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
  const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
  return luminance > 0.4 ? '#000000' : '#ffffff'
}

export function chipStyle(color: string | null): Record<string, string> | undefined {
  return color ? { backgroundColor: color, color: readableTextColor(color) } : undefined
}

// The row of the tag table a name belongs to: the tag with that name or one of its aliases, ignoring case.
export function findTagByName(tags: Tag[], name: string): Tag | undefined {
  const wanted = name.trim().toLowerCase()
  if (!wanted) return undefined
  return tags.find(
    (tag) =>
      tag.name.toLowerCase() === wanted ||
      tag.aliases.some((alias) => alias.alias.toLowerCase() === wanted),
  )
}

// Where a tag is edited: its row in the tag table of the settings (there is no page per tag).
export function tagEditLocation(name: string): RouteLocationRaw {
  return { name: 'settings-section', params: { section: 'tags' }, query: { tag: name } }
}
